'use client'

import React from 'react'
import NextLink from 'next/link'
import { Link as LocaleLink } from '@/i18n/routing'
import { cn } from '@/utilities/cn'
import styles from './GlowButton.module.css'

interface GlowButtonProps {
  children: React.ReactNode
  href?: string
  className?: string
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  ariaLabel?: string
  /** Visual variant. 'default' keeps the original green/purple glow; 'gold' is amber/gold. */
  variant?: 'default' | 'gold'
}

/**
 * GlowButton — premium CTA with animated green/purple border glow.
 * Renders as <Link> when href is provided, otherwise as <button>.
 * The glow is a rotating conic-gradient pseudo-element that animates
 * only when the user hovers (or the component has .autoGlow).
 * Fully respects prefers-reduced-motion.
 */
export const GlowButton: React.FC<GlowButtonProps> = ({
  children,
  href,
  className,
  onClick,
  type = 'button',
  disabled,
  ariaLabel,
  variant = 'default',
}) => {
  const rootClass = cn(styles.btn, variant === 'gold' && styles.gold, className)

  const inner = (
    <span className={styles.inner}>
      <span className={styles.arrowLeft} aria-hidden="true">&#8594;</span>
      <span className={styles.label}>{children}</span>
      <span className={styles.arrowRight} aria-hidden="true">&#8594;</span>
    </span>
  )

  if (href) {
    // Internal paths go through the locale-aware Link so they keep the current
    // locale (no extra redirect for "/shop", and a CMS link saved as "/de/..."
    // stays in /en on the English site). External URLs use the plain Link.
    // SITE-AUDIT-2026-11-04 #27.
    const isInternal = href.startsWith('/') && !href.startsWith('//')
    if (isInternal) {
      const localeFree = href.replace(/^\/(?:de|en)(?=\/|$|\?|#)/, '') || '/'
      return (
        <LocaleLink href={localeFree} aria-label={ariaLabel} className={rootClass}>
          <span className={styles.glowRing} aria-hidden="true" />
          {inner}
        </LocaleLink>
      )
    }
    return (
      <NextLink
        href={href}
        aria-label={ariaLabel}
        className={rootClass}
      >
        <span className={styles.glowRing} aria-hidden="true" />
        {inner}
      </NextLink>
    )
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={rootClass}
    >
      <span className={styles.glowRing} aria-hidden="true" />
      {inner}
    </button>
  )
}
